import { useState, useEffect, useCallback } from 'react';
import { api, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  CalendarDays, Users, Clock, Search, Filter, Check, XCircle,
  AlertTriangle, Loader2, Plus, X, Phone, Mail, MapPin, User,
  ChevronDown, Download, BarChart3, TrendingUp, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const MOIS_FR = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
const JOURS_FULL = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

const STATUS_CONFIG = {
  confirmed: { label: 'Confirmee', color: 'text-green-400', bg: 'bg-green-500/10', dot: 'bg-green-400' },
  pending: { label: 'En attente', color: 'text-yellow-400', bg: 'bg-yellow-500/10', dot: 'bg-yellow-400' },
  canceled: { label: 'Annulee', color: 'text-red-400', bg: 'bg-red-500/10', dot: 'bg-red-400' },
};

const HEURES = [];
for (let h = 11; h <= 14; h++) { HEURES.push(`${h}:00`, `${h}:30`); }
for (let h = 19; h <= 22; h++) { HEURES.push(`${h}:00`, `${h}:30`); }

const AdminResaPage = () => {
  const [resas, setResas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [selectedResa, setSelectedResa] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState('week');

  // Create form
  const [form, setForm] = useState({
    guest_name: '', guest_email: '', guest_phone: '',
    date: new Date().toISOString().slice(0, 10), time_slot: '12:00',
    guests_count: 2, zone: '', special_requests: '',
  });
  const [saving, setSaving] = useState(false);

  const fetchResas = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterDate) params.append('date', filterDate);
      if (search) params.append('search', search);
      const res = await api.get(`/resa/admin/reservations?${params}`);
      setResas(res.data || []);
    } catch { setResas([]); }
  }, [filterStatus, filterDate, search]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get(`/resa/stats?period=${statsPeriod}`);
      setStats(res.data);
    } catch { setStats(null); }
  }, [statsPeriod]);

  useEffect(() => {
    Promise.all([fetchResas(), fetchStats()]).finally(() => setLoading(false));
  }, [fetchResas, fetchStats]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.put(`/resa/admin/reservations/${id}/status`, { status: newStatus });
      toast.success(`Statut mis a jour`);
      fetchResas();
      fetchStats();
      if (selectedResa?.id === id) {
        setSelectedResa(prev => ({ ...prev, status: newStatus }));
      }
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette reservation ?')) return;
    try {
      await api.delete(`/resa/admin/reservations/${id}`);
      toast.success('Supprimee');
      setSelectedResa(null);
      fetchResas();
      fetchStats();
    } catch { toast.error('Erreur'); }
  };

  const handleCreate = async () => {
    if (!form.guest_name) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      await api.post('/resa/admin/reservations', form);
      toast.success('Reservation creee');
      setShowCreate(false);
      fetchResas();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  const handleExport = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/resa/export/csv`;
    window.open(url, '_blank');
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-[rgb(var(--lm-gold))]" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-resa-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarDays size={18} className="text-[rgb(var(--lm-gold))]" />
          <h1 className="text-lg font-title text-[rgb(var(--lm-text))]">Reservations</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExport} variant="outline" size="sm"
            className="border-[rgb(var(--lm-border))] gap-1" data-testid="export-csv">
            <Download size={12} /> CSV
          </Button>
          <Button onClick={() => setShowCreate(true)} size="sm"
            className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 gap-1" data-testid="new-resa-btn">
            <Plus size={14} /> Nouvelle
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="resa-stats">
          <div className="lm-card p-3 text-center">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Aujourd'hui</p>
            <p className="font-mono text-xl text-[rgb(var(--lm-gold))]">{stats.today.total}</p>
          </div>
          <div className="lm-card p-3 text-center">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">En attente</p>
            <p className="font-mono text-xl text-yellow-400">{stats.today.pending}</p>
          </div>
          <div className="lm-card p-3 text-center">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Confirmees</p>
            <p className="font-mono text-xl text-green-400">{stats.today.confirmed}</p>
          </div>
          <div className="lm-card p-3 text-center">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Couverts</p>
            <p className="font-mono text-xl text-[rgb(var(--lm-text))]">{stats.today.covers}</p>
          </div>
        </div>
      )}

      {/* KPIs */}
      {stats?.kpis && (
        <div className="grid grid-cols-3 gap-2">
          <div className="lm-card p-3 text-center">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Taux confirmation</p>
            <p className="font-mono text-lg text-[rgb(var(--lm-green))]">{stats.kpis.confirmation_rate}%</p>
          </div>
          <div className="lm-card p-3 text-center">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Moy. couverts</p>
            <p className="font-mono text-lg text-[rgb(var(--lm-gold))]">{stats.kpis.avg_covers}</p>
          </div>
          <div className="lm-card p-3 text-center">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Annulations</p>
            <p className="font-mono text-lg text-[rgb(var(--lm-red))]">{stats.kpis.cancel_rate}%</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="lm-card p-3 flex flex-wrap items-center gap-2" data-testid="resa-filters">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] pl-9 h-8 text-xs"
            placeholder="Rechercher nom, email, tel..." data-testid="search-resa" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="h-8 px-2 rounded-sm bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-xs text-[rgb(var(--lm-text))]"
          data-testid="filter-status">
          <option value="">Tous les statuts</option>
          <option value="confirmed">Confirmees</option>
          <option value="pending">En attente</option>
          <option value="canceled">Annulees</option>
        </select>
        <Input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)}
          className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] h-8 text-xs w-36"
          data-testid="filter-date" />
      </div>

      {/* Reservation list + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* List */}
        <div className="lg:col-span-2 space-y-1" data-testid="resa-list">
          {resas.length === 0 ? (
            <div className="lm-card p-8 text-center">
              <p className="text-sm text-[rgb(var(--lm-text-muted))]">Aucune reservation</p>
            </div>
          ) : (
            resas.map(r => {
              const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
              const dateObj = new Date(r.date + 'T00:00:00');
              const isSelected = selectedResa?.id === r.id;

              return (
                <button key={r.id} onClick={() => setSelectedResa(r)}
                  className={`w-full text-left lm-card p-3 flex items-center gap-3 transition-all ${
                    isSelected ? 'ring-1 ring-[rgb(var(--lm-gold)/0.4)]' : 'hover:bg-[rgb(var(--lm-surface-elevated)/0.5)]'
                  } ${r.status === 'canceled' ? 'opacity-50' : ''}`}
                  data-testid={`resa-row-${r.id}`}>
                  {/* Status dot */}
                  <div className={`w-2 h-2 rounded-full shrink-0 ${cfg.dot}`} />

                  {/* Date column */}
                  <div className="w-16 shrink-0 text-center">
                    <p className="text-xs font-mono text-[rgb(var(--lm-gold))]">{r.time_slot}</p>
                    <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">
                      {dateObj.getDate()} {MOIS_FR[dateObj.getMonth()]}
                    </p>
                  </div>

                  {/* Name + details */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[rgb(var(--lm-text))] font-medium truncate">{r.guest_name}</p>
                    <div className="flex items-center gap-3 text-[10px] text-[rgb(var(--lm-text-muted))]">
                      <span className="flex items-center gap-0.5"><Users size={9} /> {r.guests_count}</span>
                      {r.zone && <span className="flex items-center gap-0.5"><MapPin size={9} /> {r.zone}</span>}
                      {r.table_name && <span>{r.table_name}</span>}
                    </div>
                  </div>

                  {/* Status badge */}
                  <span className={`text-[9px] px-2 py-0.5 rounded-sm shrink-0 ${cfg.bg} ${cfg.color}`}>
                    {cfg.label}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Detail panel */}
        <div className="space-y-3" data-testid="resa-detail">
          {selectedResa ? (
            <div className="lm-card p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-title text-[rgb(var(--lm-text))]">{selectedResa.guest_name}</h3>
                <button onClick={() => setSelectedResa(null)} className="text-[rgb(var(--lm-text-muted))]">
                  <X size={14} />
                </button>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-[rgb(var(--lm-text-secondary))]">
                  <CalendarDays size={13} className="text-[rgb(var(--lm-gold))]" />
                  {(() => { const d = new Date(selectedResa.date + 'T00:00:00'); return `${JOURS_FULL[d.getDay()]} ${d.getDate()} ${MOIS_FR[d.getMonth()]}`; })()}
                </div>
                <div className="flex items-center gap-2 text-sm text-[rgb(var(--lm-text-secondary))]">
                  <Clock size={13} className="text-[rgb(var(--lm-gold))]" /> {selectedResa.time_slot}
                </div>
                <div className="flex items-center gap-2 text-sm text-[rgb(var(--lm-text-secondary))]">
                  <Users size={13} className="text-[rgb(var(--lm-gold))]" /> {selectedResa.guests_count} convives
                </div>
                {selectedResa.guest_email && (
                  <div className="flex items-center gap-2 text-sm text-[rgb(var(--lm-text-secondary))]">
                    <Mail size={13} className="text-[rgb(var(--lm-gold))]" /> {selectedResa.guest_email}
                  </div>
                )}
                {selectedResa.guest_phone && (
                  <div className="flex items-center gap-2 text-sm text-[rgb(var(--lm-text-secondary))]">
                    <Phone size={13} className="text-[rgb(var(--lm-gold))]" /> {selectedResa.guest_phone}
                  </div>
                )}
                {selectedResa.zone && (
                  <div className="flex items-center gap-2 text-sm text-[rgb(var(--lm-text-secondary))]">
                    <MapPin size={13} className="text-[rgb(var(--lm-gold))]" /> {selectedResa.zone}
                  </div>
                )}
                {selectedResa.special_requests && (
                  <div className="text-xs text-[rgb(var(--lm-text-muted))] italic mt-2 p-2 bg-[rgb(var(--lm-bg))] rounded-sm">
                    "{selectedResa.special_requests}"
                  </div>
                )}
              </div>

              {/* Status actions */}
              <div className="border-t border-[rgb(var(--lm-border)/0.3)] pt-3 space-y-2">
                <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Changer le statut</p>
                <div className="flex gap-1">
                  {selectedResa.status !== 'confirmed' && (
                    <Button size="sm" onClick={() => handleStatusChange(selectedResa.id, 'confirmed')}
                      className="flex-1 bg-green-500/10 text-green-400 hover:bg-green-500/20 text-xs gap-1"
                      data-testid="btn-confirm">
                      <Check size={12} /> Confirmer
                    </Button>
                  )}
                  {selectedResa.status !== 'pending' && selectedResa.status !== 'canceled' && (
                    <Button size="sm" onClick={() => handleStatusChange(selectedResa.id, 'pending')}
                      className="flex-1 bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 text-xs gap-1"
                      data-testid="btn-pending">
                      <AlertTriangle size={12} /> Attente
                    </Button>
                  )}
                  {selectedResa.status !== 'canceled' && (
                    <Button size="sm" onClick={() => handleStatusChange(selectedResa.id, 'canceled')}
                      className="flex-1 bg-red-500/10 text-red-400 hover:bg-red-500/20 text-xs gap-1"
                      data-testid="btn-cancel">
                      <XCircle size={12} /> Annuler
                    </Button>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={() => handleDelete(selectedResa.id)}
                  className="w-full border-[rgb(var(--lm-red)/0.3)] text-[rgb(var(--lm-red))] text-xs mt-2"
                  data-testid="btn-delete-resa">
                  Supprimer
                </Button>
              </div>
            </div>
          ) : (
            <div className="lm-card p-8 text-center">
              <Eye size={20} className="mx-auto mb-2 text-[rgb(var(--lm-text-muted))]" />
              <p className="text-xs text-[rgb(var(--lm-text-muted))]">Selectionnez une reservation</p>
            </div>
          )}

          {/* By hour chart */}
          {stats?.by_hour?.length > 0 && (
            <div className="lm-card p-4">
              <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Heures de pointe</p>
              <div className="flex items-end gap-1 h-20">
                {stats.by_hour.map(h => {
                  const max = Math.max(...stats.by_hour.map(x => x.count), 1);
                  const pct = (h.count / max) * 100;
                  return (
                    <div key={h.hour} className="flex-1 flex flex-col items-center gap-0.5">
                      <span className="text-[8px] font-mono text-[rgb(var(--lm-text-muted))]">{h.count}</span>
                      <div className="w-full rounded-t-sm bg-[rgb(var(--lm-gold))] transition-all"
                        style={{ height: `${Math.max(pct, 4)}%` }} />
                      <span className="text-[7px] text-[rgb(var(--lm-text-muted))]">{h.hour}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowCreate(false)}>
          <div className="lm-card w-full max-w-md mx-4 p-5 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-resa-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-title text-[rgb(var(--lm-text))]">Nouvelle reservation</h3>
              <button onClick={() => setShowCreate(false)} className="text-[rgb(var(--lm-text-muted))]"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Nom *</label>
                <Input value={form.guest_name} onChange={e => setForm(f => ({ ...f, guest_name: e.target.value }))}
                  className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="create-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Email</label>
                  <Input value={form.guest_email} onChange={e => setForm(f => ({ ...f, guest_email: e.target.value }))}
                    className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="create-email" />
                </div>
                <div>
                  <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Telephone</label>
                  <Input value={form.guest_phone} onChange={e => setForm(f => ({ ...f, guest_phone: e.target.value }))}
                    className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="create-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Date</label>
                  <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                    className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="create-date" />
                </div>
                <div>
                  <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Heure</label>
                  <select value={form.time_slot} onChange={e => setForm(f => ({ ...f, time_slot: e.target.value }))}
                    className="w-full h-9 px-3 rounded-md bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-sm text-[rgb(var(--lm-text))]"
                    data-testid="create-time">
                    {HEURES.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Convives</label>
                  <Input type="number" min={1} value={form.guests_count}
                    onChange={e => setForm(f => ({ ...f, guests_count: parseInt(e.target.value) || 1 }))}
                    className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" data-testid="create-guests" />
                </div>
                <div>
                  <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Zone</label>
                  <select value={form.zone} onChange={e => setForm(f => ({ ...f, zone: e.target.value }))}
                    className="w-full h-9 px-3 rounded-md bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-sm text-[rgb(var(--lm-text))]">
                    <option value="">Automatique</option>
                    <option value="interieur">Interieur</option>
                    <option value="terrasse">Terrasse</option>
                    <option value="veranda">Veranda</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Notes</label>
                <textarea value={form.special_requests} onChange={e => setForm(f => ({ ...f, special_requests: e.target.value }))}
                  className="w-full h-16 px-3 py-2 rounded-md bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-sm text-[rgb(var(--lm-text))] resize-none"
                  data-testid="create-notes" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={() => setShowCreate(false)} variant="outline" className="flex-1 border-[rgb(var(--lm-border))]">
                  Annuler
                </Button>
                <Button onClick={handleCreate} disabled={saving}
                  className="flex-1 bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90" data-testid="create-save">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : 'Creer'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResaPage;
